import {IconDeviceGamepad2, IconHeart, IconHeartFilled} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import useAddFavoriteGame from "../../hooks/games/useAddFavoriteGame.hook";
import useDeleteFavoriteGame from "../../hooks/games/useDeleteFavoriteGame.hook";
import {useMiniGames} from "../../store/useMiniGames";
import styles from "./GameGridModal.module.css";

const GameGridModal = ({games, onGameSelect}) => {
  const {t} = useTranslation();
  const [hoveredGame, setHoveredGame] = useState(null);
  const setFavoriteGames = useMiniGames(state => state.setFavoriteGames);
  const storeFavoriteGames = useMiniGames(state => state.favoriteGames);
  const {addFavoriteGame} = useAddFavoriteGame();
  const {deleteFavoriteGame} = useDeleteFavoriteGame();

  const isGameFavorited = useCallback(
    gameId => storeFavoriteGames.some(f => f.game_id === gameId),
    [storeFavoriteGames]
  );

  const handleFavorite = useCallback(
    (e, game) => {
      e.stopPropagation();
      setFavoriteGames(
        {id: game.id, provider: game.provider, game_provider: game.provider},
        addFavoriteGame,
        deleteFavoriteGame
      );
    },
    [setFavoriteGames, addFavoriteGame, deleteFavoriteGame]
  );

  const handleGameClick = game => {
    // Pass the selected game to onGameSelect for parallel gaming
    onGameSelect(game);
  };

  const handleGameMouseEnter = gameId => {
    setHoveredGame(gameId);
  };

  const handleGameMouseLeave = () => {
    setHoveredGame(null);
  };

  if (!games || games.length === 0) {
    return (
      <div className={styles.noGamesContainer}>
        <p className={styles.noGamesText}>{t("noQuickGamesAvailable", "No games available at the moment")}</p>
      </div>
    );
  }

  return (
    <div className={styles.gameGrid}>
      {games.map((game, index) => (
        <div
          key={game.id || index}
          className={styles.gameCard}
          onClick={() => handleGameClick(game)}
          onMouseEnter={() => handleGameMouseEnter(game.id)}
          onMouseLeave={handleGameMouseLeave}
        >
          <div className={styles.gameImageContainer}>
            {game.icon ? (
              <img
                src={game.icon}
                alt={game.name || game.game_name}
                className={styles.gameImage}
                loading="lazy"
              />
            ) : (
              <div className={styles.placeholderIcon}>
                <IconDeviceGamepad2 size={24} />
              </div>
            )}

            {/* Hover overlay */}
            <div className={`${styles.gameOverlay} ${hoveredGame === game.id ? styles.gameOverlayVisible : ""}`}>
              <span className={styles.playButton}>▶ {t("playNow", "Play Now")}</span>
            </div>

            {/* Game rank badge */}
            <div className={styles.rankBadge}>#{index + 1}</div>

            {/* Favorite heart */}
            <button
              type="button"
              className={`${styles.favBtn} ${isGameFavorited(game.id) ? styles.favActive : ""}`}
              onClick={e => handleFavorite(e, game)}
              aria-label={isGameFavorited(game.id) ? "Remove from favorites" : "Add to favorites"}
            >
              {isGameFavorited(game.id) ? (
                <IconHeartFilled
                  size={12}
                  color="#ff4d6a"
                />
              ) : (
                <IconHeart
                  size={12}
                  stroke={2}
                  color="#fff"
                />
              )}
            </button>
          </div>

          <div className={styles.gameInfo}>
            <h4 className={styles.gameName}>{game.name || game.game_name || t("unknownGame", "Unknown Game")}</h4>
            <p className={styles.gameProvider}>{game.provider || t("unknownProvider", "Unknown")}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

GameGridModal.propTypes = {
  games: PropTypes.arrayOf(PropTypes.object).isRequired,
  onGameSelect: PropTypes.func.isRequired,
};

export default GameGridModal;
